import { SendOutlined, StopOutlined } from '@ant-design/icons'
import { Button, Input } from 'antd'
import { type KeyboardEvent, memo, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { isComposingKey } from '../../domain/imeComposition'
import { hasPromptText } from '../../domain/promptText'
import type { AgentApprovalPolicy } from './approvalMode'
import { ApprovalPolicySelect } from './ApprovalPolicySelect'

interface AgentPromptInputProperties {
  // 書いている指示。モードを変えても閉じても消えないよう、呼び出し側（AgentWidget）が持つ
  prompt: string
  onPromptChange: (prompt: string) => void
  isRunning: boolean
  // 停止を押して、ターンの終わりを待っているか
  isStopping: boolean
  // 今指示を送れるか（selectCanSendToOpenChat）
  canSend: boolean
  // 送ったかを返す。送らなかったら下書きを残す
  onSend: (prompt: string) => boolean
  onStop: () => void
  // 入力欄の下に出す承認モード（approvalMode.ts）。パネルでだけ出し、1行の入力欄では出さない（null）
  approvalPolicy: AgentApprovalPolicy | null
  onApprovalPolicyChange: (approvalPolicy: AgentApprovalPolicy) => void
}

/**
 * 待機中は Enter（⌘/Ctrl+Enter も同じ）で送信、Shift+Enter で改行する。
 * 実行中は下書きだけを受け付け、送信ボタンを停止ボタンに替える（ADR-107）。停止中は重ねて押せない。
 * 承認モードの選択は、パネルの入力欄の下にだけ出す。1行の入力欄に何を出すかは決まっていないので、POC と同じく出さない（#10114）
 */
const AgentPromptInputView = ({
  prompt,
  onPromptChange,
  isRunning,
  isStopping,
  canSend,
  onSend,
  onStop,
  approvalPolicy,
  onApprovalPolicyChange,
}: AgentPromptInputProperties): ReactElement => {
  const { t } = useTranslation()
  const canSubmit = canSend && hasPromptText(prompt)

  // 送った指示は消す。API が受け付けなかったら、呼び出し側が入力欄に戻す。送れないときは送らず、書いた指示は残す
  const send = (): void => {
    if (!canSubmit) {
      return
    }
    if (onSend(prompt.trim())) {
      onPromptChange('')
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    // 日本語の変換を確定する Enter では送らない。antd の onPressEnter は変換中の Enter でも呼ばれるので、keydown で判定する
    if (event.key === 'Enter' && !event.shiftKey && !isComposingKey(event.nativeEvent)) {
      event.preventDefault()
      send()
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-end gap-1">
        <Input.TextArea
          value={prompt}
          placeholder={t(`エージェントに指示（Enter で送信、Shift+Enter で改行）`)}
          autoSize={{ minRows: 1, maxRows: 6 }}
          onChange={(event) => {
            onPromptChange(event.target.value)
          }}
          onKeyDown={handleKeyDown}
        />
        {isRunning ? (
          <Button
            icon={<StopOutlined />}
            aria-label={isStopping ? t(`停止しています`) : t(`停止`)}
            loading={isStopping}
            disabled={isStopping}
            onClick={onStop}
          />
        ) : (
          <Button type="primary" icon={<SendOutlined />} aria-label={t(`送信`)} disabled={!canSubmit} onClick={send} />
        )}
      </div>
      {approvalPolicy !== null && <ApprovalPolicySelect value={approvalPolicy} onChange={onApprovalPolicyChange} />}
    </div>
  )
}

// 回答が伸びてウィジェットが描き直されても、受け取る値が同じなら描き直さない。
// 実行中のターンは isRunning、isStopping、canSend だけを受け取り、会話全体を読まない（仕様の「状態の管理」）
export const AgentPromptInput = memo(AgentPromptInputView)
